/**
 * Response shapes used by `diagnose_subscriber`.
 *
 * WHY THIS FILE EXISTS
 * --------------------
 * diagnose_subscriber read five OCS responses as if they were flat objects and
 * arrays. None of them are. Captured live from the Mango fleet (2026-08-29,
 * ICCID 8948010000020241170), every one of those reads was wrong:
 *
 *   - `subscriber.status` is a status HISTORY array, not a string, so
 *     `String(status).toUpperCase()` produced "[OBJECT OBJECT],[OBJECT OBJECT]",
 *     which never equals "ACTIVE" — every healthy subscriber was reported
 *     CRITICAL with "OCS status is [OBJECT OBJECT] (not ACTIVE)".
 *   - `listSubscriberPrepaidPackages` returns `{ packages: [...] }`, and its
 *     rows carry `active` / `useddatabyte` / `pckdatabyte` / `tsexpirationutc`,
 *     not `status` / `dataUsed` / `dataLimit` / `expirationDate`.
 *   - `subscriberNetworkEventsOverPeriod` returns an object of per-protocol
 *     answer arrays (`s6aAnswer`, `gyAnswer`, `mapAnswer`, ...), not one array,
 *     so the `Array.isArray` guard skipped the whole check and the tool never
 *     looked at the events that actually explain an outage.
 *   - the sim id lives at `sim.id`, not `simId`, so the SIM-provider check
 *     never ran.
 *   - the IMSI lives at `imsiList[].imsi`, not `imsi`, so the HLR check never
 *     ran (`imsiFromSubscriberRecord` already knew this; diagnose did not use it).
 *
 * Two of those five turned into false CRITICALs; the other three silently made
 * the diagnosis blind. The shape knowledge belongs here next to `ocsArray` and
 * `extractSubscriberStatus` rather than duplicated in each MCP app.
 */

import { ocsArray } from "./ocs-array.js";

/** Envelope keys seen for listSubscriberPrepaidPackages, most specific first. */
export const PREPAID_PACKAGE_KEYS = ["prepaidPackage", "packages", "package"] as const;

/** Per-protocol answer arrays in a subscriberNetworkEventsOverPeriod response. */
export const NETWORK_EVENT_KEYS = [
  "s6aAnswer",
  "gyAnswer",
  "mapAnswer",
  "camelAnswer",
  "mocAnswer",
  "mtcAnswer",
] as const;

function positiveSimId(raw: unknown): number | undefined {
  const n = typeof raw === "number" ? raw : typeof raw === "string" && raw.trim() !== "" ? Number(raw) : NaN;
  return Number.isSafeInteger(n) && n > 0 ? n : undefined;
}

/**
 * Read a subscriber's SIM id.
 *
 * The live `getSingleSubscriber` record nests it at `sim.id`. Older payloads
 * may expose `simId` or `sim_id`, and some callers still wrap the record under
 * `subscriber`. The subscriber's own `id` is a different OCS entity — never
 * use it. `Number(undefined)` is `NaN`, so a missing id must stay `undefined`
 * rather than being forwarded to `getSimProviderStatus` or `changeSimStatus`.
 */
export function subscriberSimId(record: unknown): number | undefined {
  if (!record || typeof record !== "object") return undefined;
  const rec = record as Record<string, unknown>;

  const sim = rec.sim;
  if (sim && typeof sim === "object") {
    const simRec = sim as Record<string, unknown>;
    const fromSim = positiveSimId(simRec.id) ?? positiveSimId(simRec.simId) ?? positiveSimId(simRec.sim_id);
    if (fromSim !== undefined) return fromSim;
  }

  const flat = positiveSimId(rec.simId) ?? positiveSimId(rec.sim_id);
  if (flat !== undefined) return flat;

  const nested = rec.subscriber;
  if (nested && typeof nested === "object" && nested !== record) return subscriberSimId(nested);
  return undefined;
}

/** Package rows from a listSubscriberPrepaidPackages response. */
export function prepaidPackageRows(data: unknown): Record<string, unknown>[] {
  return ocsArray<Record<string, unknown>>(data, PREPAID_PACKAGE_KEYS);
}

/**
 * Does the OCS row CLAIM to be active?
 *
 * This is the raw flag only. It is NOT the same question as "does this package
 * still grant anything" — see `isActivePackage`.
 */
export function isFlaggedActive(pkg: Record<string, unknown>): boolean {
  if (typeof pkg.active === "boolean") return pkg.active;
  const status = pkg.status;
  if (typeof status === "string") return status.toUpperCase() === "ACTIVE";
  return false;
}

/**
 * Has this package passed its expiry?
 *
 * Rows with no readable expiry are NOT treated as expired: an unparseable or
 * absent timestamp is missing information, and silently deciding "expired"
 * on missing data would hide live packages the same way `active` hid dead
 * ones. Unknown stays live and is a separate problem to surface.
 */
export function isExpiredPackage(
  pkg: Record<string, unknown>,
  now: Date = new Date(),
): boolean {
  const expiry = packageExpiry(pkg);
  if (!expiry) return false;
  // OCS sends naive UTC stamps ("2026-08-21T20:03:53") with no zone marker.
  // Date.parse treats those as LOCAL time, which shifts the verdict by the
  // host offset and makes the same row expired on one box and live on another.
  const hasZone = /(?:Z|[+-]\d{2}:?\d{2})$/.test(expiry);
  const ms = Date.parse(hasZone ? expiry : `${expiry}Z`);
  if (!Number.isFinite(ms)) return false;
  return ms <= now.getTime();
}

/**
 * Is this package actually granting anything right now?
 *
 * `active` IS NOT VALIDITY. Measured live on 2026-09-07 (Mango SIM ...121):
 * package 204532407 expired 2026-08-21 and still carried `active: true`
 * seventeen days later. Filtering on the flag alone therefore counts dead
 * packages as allowance, which is exactly how a subscriber hits
 * EXPIRED_ENTITLEMENT while the console reports a healthy active package.
 *
 * A package is live only when the flag says active AND the expiry has not
 * passed. Both conditions, every time.
 */
export function isActivePackage(
  pkg: Record<string, unknown>,
  now: Date = new Date(),
): boolean {
  return isFlaggedActive(pkg) && !isExpiredPackage(pkg, now);
}

/**
 * Rows OCS calls active that have in fact expired.
 *
 * These are the silent ones: they inflate the active count and mask a
 * subscriber with no real allowance. Surface them, never just drop them.
 */
export function staleActivePackages(
  pkgs: Record<string, unknown>[],
  now: Date = new Date(),
): Record<string, unknown>[] {
  return pkgs.filter((p) => isFlaggedActive(p) && isExpiredPackage(p, now));
}

function firstNumber(pkg: Record<string, unknown>, keys: readonly string[]): number {
  for (const key of keys) {
    const v = pkg[key];
    if (typeof v === "number" && Number.isFinite(v)) return v;
    if (typeof v === "string" && v.trim() !== "" && Number.isFinite(Number(v))) return Number(v);
  }
  return 0;
}

/** Bytes consumed on a package. */
export function packageDataUsed(pkg: Record<string, unknown>): number {
  return firstNumber(pkg, ["useddatabyte", "dataUsed", "dataConsumed"]);
}

/** Byte allowance of a package. 0 means unmetered or unknown. */
export function packageDataLimit(pkg: Record<string, unknown>): number {
  return firstNumber(pkg, ["pckdatabyte", "dataLimit", "dataAllowance"]);
}

/** Expiry timestamp of a package, or "" when absent. */
export function packageExpiry(pkg: Record<string, unknown>): string {
  for (const key of ["tsexpirationutc", "expirationDate", "endDate"] as const) {
    const v = pkg[key];
    if (typeof v === "string" && v.trim() !== "") return v;
  }
  return "";
}

/** Human label for a package — the template name when the row has no name. */
export function packageName(pkg: Record<string, unknown>): string {
  if (typeof pkg.name === "string" && pkg.name !== "") return pkg.name;

  const tpl = pkg.packageTemplate;
  if (tpl && typeof tpl === "object") {
    const tplName = (tpl as Record<string, unknown>).prepaidpackagetemplatename;
    if (typeof tplName === "string" && tplName !== "") return tplName;
  }

  for (const key of ["templateId", "packageTemplateId", "subscriberprepaidpackageid"] as const) {
    const v = pkg[key];
    if (v !== undefined && v !== null) return String(v);
  }
  return "unknown";
}

/** Flatten a subscriberNetworkEventsOverPeriod response into one event list. */
export function networkEventRows(data: unknown): Record<string, unknown>[] {
  if (Array.isArray(data)) return data as Record<string, unknown>[];
  if (!data || typeof data !== "object") return [];

  const obj = data as Record<string, unknown>;
  const rows: Record<string, unknown>[] = [];
  for (const key of NETWORK_EVENT_KEYS) {
    // Each answer key holds an array that is itself sometimes an array of
    // arrays (one inner array per OCS page), so flatten one level.
    for (const entry of ocsArray<unknown>(obj[key], [])) {
      if (Array.isArray(entry)) {
        for (const inner of entry) {
          if (inner && typeof inner === "object") rows.push(inner as Record<string, unknown>);
        }
      } else if (entry && typeof entry === "object") {
        rows.push(entry as Record<string, unknown>);
      }
    }
  }
  return rows;
}

/** What the network events say went wrong. */
export interface NetworkEventSummary {
  /** Total events across every protocol leg. */
  total: number;
  /** Gy answers refusing quota because the bundle is out of credit. */
  creditLimitReached: number;
  /** S6a ULR rejected 5004 — roaming not allowed on that operator. */
  roamingNotAllowed: number;
  /** MAP legs that timed out — 2G/3G signalling not completing. */
  mapTimeouts: number;
  /** Distinct operators seen, for spotting steering ping-pong. */
  operators: string[];
  /** Newest event timestamp, or "" when there are none. */
  lastEventTime: string;
}

function eventTime(row: Record<string, unknown>): string {
  for (const key of ["eventTime", "timestamp", "date"] as const) {
    const v = row[key];
    if (typeof v === "string" && v !== "") return v;
  }
  return "";
}

/**
 * Reduce raw network events to the few signals that explain an outage.
 *
 * These are exactly the signals that were sitting unread in the response while
 * the tool reported a bogus status finding instead.
 */
export function summarizeNetworkEvents(rows: Record<string, unknown>[]): NetworkEventSummary {
  const operators = new Set<string>();
  let creditLimitReached = 0;
  let roamingNotAllowed = 0;
  let mapTimeouts = 0;
  let lastEventTime = "";

  for (const row of rows) {
    const operator = row.operator;
    if (typeof operator === "string" && operator !== "" && operator !== "N/A") operators.add(operator);

    const rre = row.rreResponse;
    if (typeof rre === "string" && rre.includes("CreditLimitReached")) creditLimitReached++;

    if (String(row.code ?? "") === "5004") roamingNotAllowed++;

    if (row.protocol === "MAP" && String(row.result ?? "").toLowerCase().includes("timeout")) mapTimeouts++;

    const t = eventTime(row);
    if (t > lastEventTime) lastEventTime = t;
  }

  return {
    total: rows.length,
    creditLimitReached,
    roamingNotAllowed,
    mapTimeouts,
    operators: [...operators].sort(),
    lastEventTime,
  };
}
